/**
 * API Proxy - Updated to use New Auth System
 * Integrated with the new authentication system
 */

import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { tokenManager } from '../../../services/auth/tokenManager';

// Flag to prevent concurrent logout operations
let isAutoLogoutRunning = false;
// Track recent login to prevent immediate logout after successful login
let lastLoginTime = 0;
const LOGIN_GRACE_PERIOD = 5000; // 5 seconds grace period after login

/**
 * Get authentication token from tokenManager
 */
const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  
  // Get token from tokenManager (centralized token access)
  const tokens = tokenManager.getTokens();
  return tokens?.accessToken || null;
};

/**
 * Handle auto-logout when authentication fails
 */
const handleAutoLogout = async (reason: string): Promise<void> => {
  console.log('Auto-logout triggered:', reason);
  
  // Prevent multiple simultaneous logout operations
  if (isAutoLogoutRunning) {
    console.log('Auto-logout already in progress, skipping...');
    return;
  }
  
  // Check if we're within the grace period after a recent login
  const timeSinceLogin = Date.now() - lastLoginTime;
  if (timeSinceLogin < LOGIN_GRACE_PERIOD) {
    console.log(`Auto-logout skipped: within grace period (${timeSinceLogin}ms since login)`);
    return;
  }
  
  isAutoLogoutRunning = true;
  
  try {
    // Clear tokens using tokenManager
    const { tokenManager } = await import('../../../services/auth/tokenManager');
    tokenManager.clearTokens();
    
    // Clear Redux state for new auth system
    try {
      const store = (await import('../../../store')).default;
      const { logoutSuccess } = await import('../../../store/slices/authSlice');
      store.dispatch(logoutSuccess());
    } catch (reduxError) {
      console.warn('Failed to clear new auth Redux state:', reduxError);
    }
    
    // Clear account storage
    try {
      const { accountStorage } = await import('../../auth/utils/accountStorage');
      await accountStorage.clearAllAccounts();
      console.log('All accounts cleared from storage');
    } catch (storageError) {
      console.warn('Failed to clear account storage:', storageError);
    }
    
    // Redirect to login after a short delay to ensure cleanup completes
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }, 100);
    
  } catch (error) {
    console.error('Error during auto-logout:', error);
    // Fallback: force redirect to login
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  } finally {
    isAutoLogoutRunning = false;
  }
};

/**
 * Update the last login time to prevent immediate auto-logout
 */
export const updateLastLoginTime = (): void => {
  lastLoginTime = Date.now();
  console.log('Login time updated for apiProxy grace period');
};

/**
 * API Proxy - Updated for New Auth System
 */
const apiProxy = async (config: AxiosRequestConfig): Promise<AxiosResponse> => {
  // Get token from new auth system
  const accessToken = getAuthToken();

  if (!accessToken) {
    // No token available - user needs to login
    console.warn('apiProxy: No authentication token found in new auth system');
    await handleAutoLogout('No authentication token found');
    throw new Error('No authentication secret found');
  }

  // Add authorization header
  const requestConfig: AxiosRequestConfig = {
    ...config,
    headers: {
      ...config.headers,
      'Authorization': `Bearer ${accessToken}`,
    },
  };

  try {
    // Make the API request with retry logic
    return await makeRequestWithRetry(requestConfig);
  } catch (error) {
    // Handle authentication errors
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      console.warn('apiProxy: Received 401 Unauthorized, triggering auto-logout');
      await handleAutoLogout('API returned 401 Unauthorized');
    }
    throw error;
  }
};

/**
 * Make API request with retry logic for transient failures
 */
const makeRequestWithRetry = async (config: AxiosRequestConfig, retryCount = 0): Promise<AxiosResponse> => {
  const maxRetries = 2;
  
  try {
    return await axios(config);
  } catch (error) {
    // Retry on network errors or 5xx server errors
    if (retryCount < maxRetries && 
        (axios.isAxiosError(error) && 
         (!error.response || error.response.status >= 500))) {
      
      const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff
      console.log(`apiProxy: Request failed, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      return makeRequestWithRetry(config, retryCount + 1);
    }
    
    throw error;
  }
};

export default apiProxy;